import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Database } from '../lib/database.types';
import { Play, Calendar, Loader2, ChevronDown, ChevronUp, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

type LiveStream = Database['public']['Tables']['live_streams']['Row'];

export default function LiveStream() {
  const [currentStream, setCurrentStream] = useState<LiveStream | null>(null);
  const [pastStreams, setPastStreams] = useState<LiveStream[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedStream, setExpandedStream] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStreams();
  }, []);

  async function fetchStreams() {
    try {
      const { data: liveData } = await supabase
        .from('live_streams')
        .select('*')
        .eq('is_live', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (liveData) {
        setCurrentStream(liveData);
        setExpandedStream(liveData.id);
      }

      const { data: pastData } = await supabase
        .from('live_streams')
        .select('*')
        .eq('is_live', false)
        .order('created_at', { ascending: false })
        .limit(10);

      if (pastData) {
        setPastStreams(pastData);
      }
    } catch (error) {
      console.error('Erro ao buscar transmissões:', error);
    } finally {
      setLoading(false);
    }
  }

  function getYouTubeEmbedUrl(url: string) {
    try {
      let videoId = '';
      
      if (url.includes('watch?v=')) {
        videoId = url.split('watch?v=')[1]?.split('&')[0];
      } else if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1]?.split('?')[0];
      } else if (url.includes('embed/')) {
        videoId = url.split('embed/')[1]?.split('?')[0];
      }

      if (!videoId) {
        throw new Error('ID do vídeo não encontrado');
      }

      // Adicionando parâmetros extras para melhor compatibilidade com WebView
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&origin=${window.location.origin}`;
    } catch (error) {
      console.error('Erro ao processar URL do YouTube:', error);
      return '';
    }
  }

  function toggleStream(streamId: string) {
    setExpandedStream(expandedStream === streamId ? null : streamId);
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-brand-teal animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Transmissão Atual */}
      <div className="mb-12">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Play className="h-8 w-8 text-brand-teal" />
            <h1 className="text-3xl font-bold text-brand-teal">
              {currentStream ? 'Ao Vivo Agora' : 'Próxima Transmissão'}
            </h1>
          </div>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
          >
            <Home className="h-4 w-4" />
            <span>Voltar para Home</span>
          </button>
        </div>

        {currentStream ? (
          <div className="bg-white rounded-xl shadow-elevation-1 overflow-hidden">
            <button
              onClick={() => toggleStream(currentStream.id)}
              className="w-full p-6 text-left flex justify-between items-start hover:bg-gray-50 transition-colors"
            >
              <div>
                <h2 className="text-xl font-semibold text-brand-teal mb-2">
                  {currentStream.title}
                </h2>
                {currentStream.description && (
                  <p className="text-gray-600">{currentStream.description}</p>
                )}
              </div>
              {expandedStream === currentStream.id ? (
                <ChevronUp className="h-6 w-6 text-gray-400 flex-shrink-0" />
              ) : (
                <ChevronDown className="h-6 w-6 text-gray-400 flex-shrink-0" />
              )}
            </button>
            
            {expandedStream === currentStream.id && currentStream.youtube_url && (
              <div className="aspect-video w-full bg-gray-900">
                <iframe
                  src={getYouTubeEmbedUrl(currentStream.youtube_url)}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={currentStream.title}
                  sandbox="allow-forms allow-scripts allow-pointer-lock allow-same-origin allow-top-navigation allow-presentation"
                />
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white p-6 rounded-xl shadow-elevation-1 text-center">
            <p className="text-gray-600">
              Não há transmissão ao vivo no momento. Fique ligado para as próximas!
            </p>
          </div>
        )}
      </div>

      {/* Transmissões Anteriores */}
      {pastStreams.length > 0 && (
        <div>
          <div className="flex items-center gap-3 mb-6">
            <Calendar className="h-8 w-8 text-brand-teal" />
            <h2 className="text-2xl font-bold text-brand-teal">
              Transmissões Anteriores
            </h2>
          </div>

          <div className="space-y-4">
            {pastStreams.map((stream) => (
              <div
                key={stream.id}
                className="bg-white rounded-xl shadow-elevation-1 overflow-hidden hover:shadow-elevation-2 transition-shadow"
              >
                <button
                  onClick={() => toggleStream(stream.id)}
                  className="w-full p-6 text-left flex justify-between items-start hover:bg-gray-50 transition-colors"
                >
                  <div>
                    <h3 className="font-semibold text-brand-teal mb-2">
                      {stream.title}
                    </h3>
                    {stream.description && (
                      <p className="text-sm text-gray-600">{stream.description}</p>
                    )}
                    <div className="mt-2 text-sm text-gray-500">
                      {new Date(stream.created_at).toLocaleDateString('pt-BR')}
                    </div>
                  </div>
                  {expandedStream === stream.id ? (
                    <ChevronUp className="h-6 w-6 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-6 w-6 text-gray-400 flex-shrink-0" />
                  )}
                </button>

                {expandedStream === stream.id && stream.youtube_url && (
                  <div className="aspect-video w-full bg-gray-900">
                    <iframe
                      src={getYouTubeEmbedUrl(stream.youtube_url)}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={stream.title}
                      sandbox="allow-forms allow-scripts allow-pointer-lock allow-same-origin allow-top-navigation allow-presentation"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}